import React from 'react';
import { subjectBorderClass } from '../../shared/constants/subjects';
import { subjectAbbrev } from '../../shared/constants/subjects';

// ─── Border class helper ──────────────────────────────────────────────────────

function borderClass(entry, colourMode) {
  if (colourMode === 'subject') return subjectBorderClass(entry.subject_area);
  const s = entry.status;
  if (s === 'completed') return 'border-completed';
  if (s === 'moved')     return 'border-moved';
  const t = entry.planner_type;
  if (t === 'new') return 'border-new';
  const conf = entry.topic_confidence;
  if (conf == null || conf < 0.4) return 'border-revision-weak';
  if (conf < 0.7) return 'border-revision-moderate';
  return 'border-revision-strong';
}

// ─── Knowledge dot ────────────────────────────────────────────────────────────

function KnowledgeDot({ conf }) {
  let cls = 'knowledge-dot-none';
  if (conf != null) {
    if (conf < 0.4)      cls = 'knowledge-dot-weak';
    else if (conf < 0.7) cls = 'knowledge-dot-moderate';
    else                  cls = 'knowledge-dot-strong';
  }
  return <div className={`knowledge-dot ${cls}`} />;
}

// ─── Size bars ────────────────────────────────────────────────────────────────

function SizeBars({ principleCount }) {
  const pc = principleCount || 0;
  let tier = 'small';
  if (pc >= 15) tier = 'large';
  else if (pc >= 8) tier = 'medium';

  return (
    <div className="size-bars">
      <div className={`size-bar size-bar-1 size-bar-${tier}`} />
      <div className={`size-bar size-bar-2 ${tier === 'medium' || tier === 'large' ? `size-bar-${tier}` : 'size-bar-off'}`} />
      <div className={`size-bar size-bar-3 ${tier === 'large' ? 'size-bar-large' : 'size-bar-off'}`} />
    </div>
  );
}

// ─── Confidence colour ────────────────────────────────────────────────────────

function confColourClass(conf) {
  if (conf >= 0.7) return 'conf-green';
  if (conf >= 0.4) return 'conf-amber';
  return 'conf-red';
}

// ─── Last seen formatter ──────────────────────────────────────────────────────

function formatLastSeen(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

function lastSeenColour(dateStr) {
  if (!dateStr) return 'card-stat-muted';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff <= 7)  return 'card-stat-green';
  if (diff <= 21) return 'card-stat-amber';
  return 'card-stat-red';
}

// ─── MCQ bar ─────────────────────────────────────────────────────────────────

function McqBar({ correct, total }) {
  if (!total) return null;
  const segs = Array.from({ length: Math.min(total, 5) }, (_, i) => (
    <span key={i} className={`mcq-seg ${i < correct ? 'mcq-seg-correct' : 'mcq-seg-wrong'}`} />
  ));
  return <span className="mcq-bar">{segs}</span>;
}

// ─── WeekCard v6 ──────────────────────────────────────────────────────────────

export default function WeekCard({ entry, dateStr, detailLevel, colourMode, onSwitchToDayView }) {
  const e = entry;
  const isCompleted = e.status === 'completed';
  const isMoved     = e.status === 'moved';
  const isRevision  = e.planner_type === 'revision';
  const conf        = e.topic_confidence;
  const abbrev      = subjectAbbrev(e.subject_area);
  const border      = borderClass(e, colourMode || 'status');
  const level       = detailLevel || 'standard';
  const switchTo    = () => onSwitchToDayView && onSwitchToDayView(dateStr);

  // ── Completed ──
  if (isCompleted) {
    const doneConf = e.confidence_on_complete;
    return (
      <div className={`week-card-completed ${border}`} onClick={switchTo} role="button" tabIndex={0}>
        {/* Row 1: name + tick + conf */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 4 }}>
          <span className="card-name-done-week">{e.subtopic || e.display_name}</span>
          <div className="done-tick" style={{ flexShrink: 0 }}>
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          {doneConf != null && (
            <span className="done-conf">{Math.round(doneConf * 100)}%</span>
          )}
        </div>
        {/* Row 2: knowledge dot + subject */}
        <div className="card-row2">
          <div className="knowledge-dot knowledge-dot-strong" />
          <span className="card-subject-week">{abbrev}</span>
        </div>
        {/* Row 3: L2 text — only if not a study unit (general_topic is cleared for study units) */}
        {e.general_topic && (
          <div className="card-l2-row-week">{e.general_topic}</div>
        )}
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`week-card-moved ${border}`} onClick={switchTo} role="button" tabIndex={0}
        onKeyDown={ev => ev.key === 'Enter' && switchTo()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="card-name-moved-week">{e.subtopic || e.display_name}</span>
          <span className="moved-label">Missed</span>
        </div>
      </div>
    );
  }

  // ── Pending (new or revision) ──
  const cardClass = isRevision ? `week-card-revision ${border}` : `week-card-new ${border}`;

  return (
    <div className={cardClass} onClick={switchTo} role="button" tabIndex={0}
      onKeyDown={ev => ev.key === 'Enter' && switchTo()}>

      {/* Row 1: name + pill/conf */}
      <div className="card-row1">
        <span className="card-name-week">{e.subtopic || e.display_name}</span>
        {isRevision ? (
          conf != null
            ? <span className={`conf-value-sm ${confColourClass(conf)}`}>{Math.round(conf * 100)}%</span>
            : <span className="conf-value-sm conf-none">—</span>
        ) : (
          <span className="pill-new-sm">New</span>
        )}
      </div>

      {/* Row 2: knowledge dot + subject */}
      <div className="card-row2">
        <KnowledgeDot conf={conf} />
        <span className="card-subject-week">{abbrev}</span>
      </div>

      {/* Row 3: L2 text (standard/full only, not for study units) */}
      {(level === 'standard' || level === 'full') && e.general_topic && (
        <div className="card-l2-row-week">{e.general_topic}</div>
      )}

      {/* Row 4: size bars + revise pill + conf ring (standard/full) */}
      {(level === 'standard' || level === 'full') && (
        <div className="card-row3">
          <SizeBars principleCount={e.principle_count} />
          {isRevision && <span className="pill-revise-sm">Revise</span>}
        </div>
      )}

      {/* Row 5: stats (full only) */}
      {level === 'full' && (
        <div className="card-extra">
          <div className="card-stat">
            <span className="card-stat-label">Questions</span>
            {e.questions_attempted > 0 ? (
              <span className={`card-stat-value ${e.questions_correct / e.questions_attempted >= 0.7 ? 'card-stat-green' : e.questions_correct / e.questions_attempted >= 0.4 ? 'card-stat-amber' : 'card-stat-red'}`}>
                {e.questions_correct}/{e.questions_attempted}
                <McqBar correct={e.questions_correct} total={e.questions_attempted} />
              </span>
            ) : (
              <span className="card-stat-value card-stat-muted">None yet</span>
            )}
          </div>
          <div className="card-stat">
            <span className="card-stat-label">Last seen</span>
            <span className={`card-stat-value ${lastSeenColour(e.date_updated)}`}>
              {formatLastSeen(e.date_updated)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
